import { Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DAYTIMES } from '../../models/time.model';
import * as moment from 'moment';

import { Validators, UntypedFormControl } from '@angular/forms';
import { UntypedFormBuilder } from '@angular/forms';
import { TimeValidator } from 'src/app/validators/time.validator';
import { EventsService } from 'src/app/services/events.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Constants } from 'src/app/constants/constants';
import { EventDetails } from 'src/app/models/events.model';
import { noWhitespaceValidator } from 'src/app/validators/blank-space.validator';
import { CalendarUtilsService } from 'src/app/services/calendar-utils.service';
import { AgendaToggleService } from 'src/app/services/agenda-toggle.service';

@Component({
  selector: 'transflo-scheduler-modal',
  templateUrl: './scheduler-modal.component.html',
  styles: []
})
export class SchedulerModalComponent implements OnInit, OnChanges {
  constants = Constants;
  public todayDate = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  };
  public times = DAYTIMES;
  eventDetails: any = {};
  eventtypes = [];
  @Output() closeModal = new EventEmitter();
  @Input() currentEvent: EventDetails;
  @Input() shouldUpdateDelete: boolean;

  eventForm = this.fb.group({
    eventType: ['Personal', [Validators.required]],
    eventName: ['', [Validators.required, Validators.maxLength(60), noWhitespaceValidator]],
    eventDesc: ['', [Validators.maxLength(60)]],
    eventDate: ['', [Validators.required]],
    eventStartTime: [null, [Validators.required]],
    eventEndTime: [null, [Validators.required]],
    eventFlag: [false],
  }, { validator: TimeValidator });

  constructor(
    private fb: UntypedFormBuilder,
    private eventService: EventsService,
    private notificationService: NotificationService,
    private eventsService: EventsService,
    private calendarUtils: CalendarUtilsService,
    private agendaToggleService: AgendaToggleService
  ) { }

  ngOnInit(): void {
    this.eventsService.getEventTypes()
      .subscribe(result => this.eventtypes = result);
    this.eventForm.valueChanges
      .subscribe(arg => {
        if (this.eventForm.get('editflag')) {
          this.eventForm.patchValue({ editflag: true }, { emitEvent: false });
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentEvent) {
      const startDate = moment(this.currentEvent.StartDateTime);
      const currentEventDate = {
        year: parseInt(startDate.format('YYYY'), 10),
        month: parseInt(startDate.format('M'), 10), day: parseInt(startDate.format('DD'), 10)
      };
      this.eventForm.addControl('editflag', new UntypedFormControl(false, Validators.requiredTrue));
      this.eventForm.patchValue({
        eventName: this.currentEvent.Title || '',
        eventDesc: this.currentEvent.Description || '',
        eventDate: currentEventDate || '',
        eventFlag: this.currentEvent.IsFlagged || false,
        eventStartTime: moment(this.currentEvent.StartDateTime).minute() !== 0 && moment(this.currentEvent.StartDateTime).minute() !== 30 ?
          // tslint:disable-next-line:max-line-length
          moment(this.currentEvent.StartDateTime).add(30 - (moment(this.currentEvent.StartDateTime).minute() % 30), 'minutes').format('HH:mm') || '' :
          moment(this.currentEvent.StartDateTime).format('HH:mm') || '',
        eventEndTime: moment(this.currentEvent.EndDateTime).minute() !== 0 && moment(this.currentEvent.EndDateTime).minute() !== 30 ?
          // tslint:disable-next-line:max-line-length
          moment(this.currentEvent.EndDateTime).add(30 - (moment(this.currentEvent.EndDateTime).minute() % 30), 'minutes').format('HH:mm') || '' :
          moment(this.currentEvent.EndDateTime).format('HH:mm') || '',
        eventType: this.capitalizeFirstLetter(this.currentEvent.EventType) || ''
      });
    }
  }

  get eventName() { return this.eventForm.get('eventName'); }
  get eventDesc() { return this.eventForm.get('eventDesc'); }
  get eventType() { return this.eventForm.get('eventType'); }
  get eventDate() { return this.eventForm.get('eventDate'); }
  get eventStartTime() { return this.eventForm.get('eventStartTime'); }
  get eventEndTime() { return this.eventForm.get('eventEndTime'); }
  get eventFlag() { return this.eventForm.get('eventFlag'); }

  dismissModal(event: any): void {
    event.preventDefault();
    this.closeModal.emit(true);
  }

  getFormattedDate(date: Date, time: string) {
    const timeArray = time.split(':');
    date.setHours(parseInt(timeArray[0], 10));
    date.setMinutes(parseInt(timeArray[1], 10));
    date.setSeconds(0);
    date.setMilliseconds(0);
    const formattedDate = moment.utc(new Date(date.getTime())); // Convert the date to UTC
    //formattedDate.set({ hour: parseInt(timeArray[0], 10), minute: parseInt(timeArray[1], 10), second: 0, millisecond: 0 });
    return formattedDate.format().substring(0, 19);
  }

  getEventAPIFormatted(): any {
    const eventdate = new Date(parseInt(this.eventDate.value.year, 10),
      parseInt(this.eventDate.value.month, 10) - 1, parseInt(this.eventDate.value.day, 10));
    return {
      Title: this.eventName.value,
      Description: this.eventDesc.value,
      IsFlagged: this.eventFlag.value,
      EventType: this.eventType.value,
      StartDateTime: this.getFormattedDate(eventdate, this.eventStartTime.value),
      EndDateTime: this.getFormattedDate(eventdate, this.eventEndTime.value)
    };
  }

  onSubmit(): void {
    this.eventService.addEvent(this.getEventAPIFormatted())
      .subscribe(resp => {
        if (resp && resp.status === 201) {
          this.closeModal.emit(true);
          this.notificationService.newNotification({
            eventAddedNotificationLabel: Constants.eventAddedNotificationLabel,
            eventAddedNotificationClass: Constants.eventAddedNotificationClass
          });
          this.setAgendaView();
          const eventdate = new Date(parseInt(this.eventDate.value.year, 10),
            parseInt(this.eventDate.value.month, 10) - 1, parseInt(this.eventDate.value.day, 10));
          this.goToCalendarDate(eventdate);
          this.calendarUtils.calendarDaysSubject.next(true);
        }
      });
  }

  setAgendaView(): void {
    this.agendaToggleService.newFlagedValue(this.eventFlag.value);
    this.agendaToggleService.newNormalValue(!this.eventFlag.value);
  }

  capitalizeFirstLetter(seq: string): string {
    return seq.charAt(0).toUpperCase() + seq.slice(1);
  }

  deleteEvent(): void {
    this.eventService.deleteEvent(this.currentEvent.Id)
      .subscribe(resp => {
        if (resp && resp.status === 204) {
          this.closeModal.emit(true);
          this.notificationService.newNotification({
            eventAddedNotificationLabel: Constants.eventDeletedNotificationLabel,
            eventAddedNotificationClass: Constants.eventDeletedNotificationClass
          });
          this.goToCalendarDate(this.currentEvent.StartDateTime);
          this.calendarUtils.calendarDaysSubject.next(true);
        }
      });
  }

  modifyEvent(): void {
    this.eventService.updateEvent(this.getEventAPIFormatted(), this.currentEvent.Id)
      .subscribe(resp => {
        if (resp && resp.status === 204) {
          this.closeModal.emit(true);
          this.notificationService.newNotification({
            eventAddedNotificationLabel: Constants.eventUpdatedNotificationLabel,
            eventAddedNotificationClass: Constants.eventAddedNotificationClass
          });
          this.setAgendaView();
          this.goToCalendarDate(this.currentEvent.StartDateTime);
          this.calendarUtils.calendarDaysSubject.next(true);
        }
      });
  }

  goToCalendarDate(date: any): void {
    setTimeout(() => {
      this.calendarUtils.calendarDayClicked.next(date);
    }, 0);
  }
}
